import { cva, type VariantProps } from 'class-variance-authority'
import React from 'react'

import { cn } from '@/lib/cn'

const Stepper = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <section
    ref={ref}
    className={cn('flex flex-col space-y-10', className)}
    {...props}
  />
))
Stepper.displayName = 'Stepper'

const stepperItemVariants = cva(
  'relative flex items-center gap-2 rounded-sm px-2 py-1 before:absolute before:left-6 before:top-[calc(50%+16px)] before:z-10 before:h-[calc(100%+8px)] before:w-[2px] before:bg-system-border-primary last:before:hidden',
  {
    variants: {
      variant: {
        default: 'cursor-pointer bg-white',
        active: 'cursor-default bg-white',
        disabled: 'cursor-not-allowed bg-white',
        create: 'cursor-pointer border-0 py-0',
      },
      status: {
        default: '',
        completed: 'before:bg-green-500',
      },
    },
    defaultVariants: {
      variant: 'default',
      status: 'default',
    },
  },
)

const StepperItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> &
    VariantProps<typeof stepperItemVariants>
>(({ className, variant, status, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(stepperItemVariants({ variant, status }), className)}
    {...props}
  />
))
StepperItem.displayName = 'StepperItem'

const stepperCircleVariants = cva(
  'flex size-8 shrink-0 items-center justify-center rounded-full text-sm text-system-text-primary',
  {
    variants: {
      variant: {
        default:
          'border-2 border-system-border-primary bg-white text-system-text-primary',
        active: 'bg-system-primary-purple text-white',
        completed: 'bg-green-500 text-white',
        disabled: 'border-0 bg-system-grey-strong text-white',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

const StepperCircle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> &
    VariantProps<typeof stepperCircleVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(stepperCircleVariants({ variant }), className)}
    {...props}
  />
))
StepperCircle.displayName = 'StepperCircle'

const stepperTextVariants = cva('text-pretty text-sm font-medium', {
  variants: {
    variant: {
      default: 'text-system-text-primary',
      active: 'font-bold text-system-primary-purple',
      completed: 'text-system-text-primary',
      disabled: 'text-system-text-secondary',
      create:
        'flex items-center justify-center rounded-lg border border-system-border-secondary px-2 py-1 text-xs font-normal text-system-primary-purple shadow-lg',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const StepperText = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement> &
    VariantProps<typeof stepperTextVariants>
>(({ className, variant, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(stepperTextVariants({ variant }), className)}
    {...props}
  />
))
StepperText.displayName = 'StepperText'

export { Stepper, StepperItem, StepperCircle, StepperText }
